import { gql, type TypedDocumentNode } from '@apollo/client';
import classNames from 'classnames';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import styled from 'styled-components';

import {
  ManagerProgressionTaskState,
  TaskLineupType,
} from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Refresh } from '@sorare/core/src/atoms/icons/Refresh';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography';
import { ArrowDownShape as ArrowDownShapeSvg } from '@sorare/core/src/atoms/ui/ArrowDownShape/index.tsx';
import { OnboardingHighlightWrapper } from '@sorare/core/src/components/onboarding/OnboardingHighlightWrapper';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText';
import { useLifecycle } from '@sorare/core/src/hooks/useLifecycle';

import { ReplayIntroDialog } from '#src/components/series/ReplayIntroDialog/index.tsx';
import { Lifes } from '#src/components/series/SeriesBoard/Lifes/index.tsx';
import { SeriesBoardOnboarding } from '#src/components/series/SeriesBoard/SeriesBoardOnboarding/index.tsx';

import { CheckmarkTile } from './CheckmarkTile/index.tsx';
import { ClaimableTile } from './ClaimableTile/index.tsx';
import { FailedTile } from './FailedTile/index.tsx';
import { ReadyAndSelectedTile } from './ReadyAndSelectedTile/index.tsx';
import { ReadyIcon, ReadyIconWrapper } from './ReadyIcon/index.tsx';
import { ReadyTile } from './ReadyTile/index.tsx';
import { TooltipContent } from './TooltipContent/index.tsx';
import type { HotStreakTile_thresholdPickerTask } from './__generated__/index.graphql.ts';
import lockedTile from './assets/lockedTile.webp';
import {
  CroppedPlayerImage,
  Dot,
  greenOverlay,
  LiveChip,
  redOverlay,
  TileWrapper,
} from './ui.tsx';

const StyledButtonBase = styled(ButtonBase)`
  z-index: 1;
`;

type Props = {
  task: HotStreakTile_thresholdPickerTask;
  showOnboarding?: boolean;
  onClaimTask: (id: string) => void;
  onOpenHotStreaksTutorialStory?: () => void;
  showTargetValue?: boolean;
  setShowTargetValue?: (showTargetValue: boolean) => void;
  lineupsPath: string;
  composePath: string;
  oldThreshold?: number | null;
};
export const HotStreakTile = ({
  task,
  showOnboarding = false,
  onClaimTask,
  onOpenHotStreaksTutorialStory,
  showTargetValue = false,
  setShowTargetValue,
  lineupsPath,
  composePath,
  oldThreshold = null,
}: Props) => {
  const navigate = useNavigate();
  const { lifecycle } = useLifecycle();
  const [openReplayIntroDialog, setOpenReplayIntroDialog] = useState(false);
  const { id, aasmState, rewardConfigs, replay, displayedPlayer, taskLineups } =
    task;

  const hasReducedThreshold =
    !replay && !!oldThreshold && oldThreshold > task.target;

  const tooltipContent = (
    <TooltipContent
      target={task.target}
      showTargetValue={showTargetValue}
      hasReducedThreshold={hasReducedThreshold}
      oldThreshold={oldThreshold}
      rewardConfig={rewardConfigs[0]}
    />
  );

  const hasSubmittedLineups = taskLineups.length > 0;
  const hasFailedAllSubmittedLineups =
    hasSubmittedLineups &&
    taskLineups.every(lineup => lineup.aasmState === TaskLineupType.FAILED);

  const hasFailedReplay =
    replay &&
    aasmState === ManagerProgressionTaskState.READY &&
    hasFailedAllSubmittedLineups;

  const to = replay ? composePath : lineupsPath;

  if (
    hasFailedReplay ||
    task.aasmState === ManagerProgressionTaskState.CANCELED
  ) {
    return (
      <StyledButtonBase to={to}>
        <TileWrapper>
          <FailedTile />
          <ArrowDownShapeSvg colors={{ start: '#721717', end: '#D81616' }}>
            <Vertical center>
              <Refresh />
              <BrandS>
                <FormattedMessage defaultMessage="Miss" />
              </BrandS>
            </Vertical>
          </ArrowDownShapeSvg>
        </TileWrapper>
      </StyledButtonBase>
    );
  }

  if (aasmState === ManagerProgressionTaskState.READY) {
    if (hasSubmittedLineups) {
      if (hasFailedAllSubmittedLineups) {
        return (
          <StyledButtonBase to={lineupsPath}>
            <TileWrapper>
              <FailedTile />
              <ArrowDownShapeSvg colors={{ start: '#721717', end: '#D81616' }}>
                <CroppedPlayerImage
                  style={{
                    background: `${redOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                  }}
                />
                <LiveChip color="black">
                  <Lifes task={task} size={1} />
                </LiveChip>
              </ArrowDownShapeSvg>
            </TileWrapper>
          </StyledButtonBase>
        );
      }

      const hasLiveGames = task.taskLineups.some(lineup => lineup.hasLiveGames);
      return (
        <StyledButtonBase to={lineupsPath}>
          <TileWrapper>
            <ReadyAndSelectedTile />
            <ArrowDownShapeSvg
              animatedOnHover
              colors={{ start: '#00F34D', end: '#00D5FF' }}
            >
              <CroppedPlayerImage
                style={{
                  background: `${greenOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                }}
              />
              {!!hasLiveGames && (
                <LiveChip color="black">
                  <Dot />
                  <BrandXXS>
                    <FormattedMessage defaultMessage="Live" />
                  </BrandXXS>
                </LiveChip>
              )}
            </ArrowDownShapeSvg>
          </TileWrapper>
        </StyledButtonBase>
      );
    }

    const onClick = () => {
      if (replay) {
        setOpenReplayIntroDialog(true);
        return;
      }
      if (!lifecycle?.sawSeriesHotStreakOnboarding) {
        onOpenHotStreaksTutorialStory?.();
        return;
      }
      navigate(to);
    };

    return (
      <>
        <OnboardingHighlightWrapper
          highlightName="SeriesBoardReplayButton"
          wrap={!!showOnboarding}
        >
          <ButtonBase
            onClick={() => {
              if (replay) {
                setOpenReplayIntroDialog(true);
                return;
              }
              if (!lifecycle?.sawSeriesHotStreakOnboarding) {
                onOpenHotStreaksTutorialStory?.();
                return;
              }
              navigate(to);
            }}
          >
            <TileWrapper>
              <ReadyTile />
              <ReadyIconWrapper className="animated">
                <Tooltip
                  open
                  placement="top"
                  onTooltipClick={() => setShowTargetValue?.(!showTargetValue)}
                  showInPortal={false}
                  title={tooltipContent}
                >
                  <ReadyIcon className={classNames({ replay })} />
                </Tooltip>
              </ReadyIconWrapper>
            </TileWrapper>
          </ButtonBase>
        </OnboardingHighlightWrapper>
        <SeriesBoardOnboarding isReplay={!!replay} onClose={onClick} />
        <ReplayIntroDialog
          task={task}
          open={openReplayIntroDialog}
          onClose={() => setOpenReplayIntroDialog(false)}
          to={to}
        />
      </>
    );
  }

  if (aasmState === ManagerProgressionTaskState.COMPLETED) {
    return (
      <StyledButtonBase
        {...(replay ? { onClick: () => onClaimTask(id) } : { to: lineupsPath })}
      >
        <TileWrapper>
          <ClaimableTile />
          <ArrowDownShapeSvg floating>
            <Vertical gap={0} center>
              <RewardConfigImage anyRewardConfig={rewardConfigs[0]} />
              <BrandS>
                <RewardConfigText anyRewardConfig={rewardConfigs[0]} />
              </BrandS>
            </Vertical>
          </ArrowDownShapeSvg>
        </TileWrapper>
      </StyledButtonBase>
    );
  }

  if (aasmState === ManagerProgressionTaskState.CLAIMED) {
    return (
      <StyledButtonBase
        to={replay ? undefined : lineupsPath}
        as={replay ? 'div' : undefined}
      >
        <TileWrapper>
          <CheckmarkTile />
          <ArrowDownShapeSvg colors={{ start: '#00F34D', end: '#00D5FF' }}>
            <CroppedPlayerImage
              style={{
                background: `${greenOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
              }}
            />
          </ArrowDownShapeSvg>
        </TileWrapper>
      </StyledButtonBase>
    );
  }

  if (aasmState === ManagerProgressionTaskState.ASSIGNED) {
    return (
      <TileWrapper>
        <Tooltip
          open
          placement="top"
          showInPortal={false}
          onTooltipClick={() => setShowTargetValue?.(!showTargetValue)}
          title={tooltipContent}
        >
          <img src={lockedTile} alt="locked tile" width={30} />
        </Tooltip>
      </TileWrapper>
    );
  }

  return null;
};

HotStreakTile.fragments = {
  thresholdPickerTask: gql`
    fragment HotStreakTile_thresholdPickerTask on ThresholdPickerTask {
      id
      aasmState
      replay
      rewardConfigs {
        id
        ...RewardConfigImage_anyRewardConfigInterface
        ...RewardConfigText_anyRewardConfigInterface
        ...TooltipContent_anyRewardConfigInterface
      }
      displayedPlayer {
        slug
        squaredPictureUrl
      }
      taskLineups {
        id
        aasmState
        hasLiveGames
      }
      ...ReplayIntroDialog_taskInterface
      ...Lifes_thresholdPickerTask
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${ReplayIntroDialog.fragments.taskInterface}
    ${Lifes.fragments.thresholdPickerTask}
    ${TooltipContent.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<HotStreakTile_thresholdPickerTask>,
};
